import { useState, useEffect, useMemo } from "react";
import { T, UI, FONT, BTN } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { fetchQuote, fetchProfile } from "../utils/finnhub";
import { fetchCompanyNews } from "../utils/finnhubNews";
import { fetchNextReport, HOUR_HE } from "../utils/nextReport";
import { fetchGeoEvents, isOfficial } from "../utils/geoEvents";
import { isAbout, eventNames } from "../utils/newsMatch";
import { isAdvisoryArticle, isAdvisoryHeadline } from "../constants/advisory";
import { useHebrewNews, waitingNote } from "../utils/headline";
import { quotedHeadline } from "../utils/voice";
import { industryToSector, readProfile, SECTOR_ETF } from "../utils/sectorClassify";
import { computeChanges, checkSnapshot, daysUntilText, NOT_COMPARABLE, NOT_COMPARABLE_WHY } from "../utils/homeChanges";
import { readBaseline } from "../lib/homeVisit";
import { usePortfolioSegments } from "../utils/exposureData";
import { computeExposure, regionRows } from "../utils/exposure";
import { REGION_HE, companyRegionDef } from "../constants/regions";
import { fmtPct, PCT_SHARE } from "../utils/formatters";
import { companyName as cleanName } from "../utils/companyName";
import Money from "./Money";
import { dateNumeric } from "../utils/formatDate";
import { useMarketStatus, lastSessionLabel } from "../utils/marketStatus";
import { timeAgoSec } from "../utils/timeAgo";
import OfficialBadge from "./OfficialBadge";
import PriceChart from "./PriceChart";
import FinancialsPanel from "./FinancialsPanel";
import HoldingBadge from "./HoldingBadge";
import Headline from "./Headline";
import LTR from "./LTR";
import Icon from "./Icon";
import { openScenarioWith } from "../utils/scenario";
import { noQuoteText, isStaleQuote } from "../utils/quoteState";
import { usePurchaseRates, shekelSince, PURCHASE_RATE_LABEL, TODAY_RATE_LABEL, PRICE_ONLY_LABEL, FX_EFFECT_LABEL } from "../utils/purchaseFx";
import { useFxRates } from "../utils/fxRates";
import { Skeleton, Loading } from "./Skeleton";
import { valuedHoldings } from "../utils/portfolioExposures";
import DualMoney, { withShekel, IlsSinceSlot } from "./DualMoney";

// ─── עמוד חברה ───────────────────────────────────────────────────────────────
// One company, through the portfolio. Opened from anywhere with the global
// "si-open-stock" event (App): the portfolio screen, the home screen, the
// news. The order is the order a holder asks in:
//
//   1 · which company        · ticker and name
//   2 · my position          · share of the portfolio, value, quantity, today, since purchase
//   3 · what changed         · since the previous visit, from the SAME snapshot the
//                              home screen compared against (lib/homeVisit.js)
//   4 · what is coming       · the next report, with the days counted
//   5 · news                 · only what passes the portfolio match (utils/newsMatch.js)
//   6 · context              · facts only: the sector and its share, revenue by
//                              region, the other holdings in that sector
//   7 · the numbers          · the company's SEC-reported figures
//
// What this page does not carry, deliberately: Finnhub's key metrics (P/E,
// EPS, beta, market cap) and analyst estimates. Whether those may be shown to
// users is an open question for counsel (ROADMAP, "לעו״ד"), and until it is
// answered they are not shown here. SEC filings are public record.
//
// No "worth checking", no "needs attention", no reading of any number.

// Sprint 17: every figure through the shared Money and the one percent rule.
const pct1 = v => fmtPct(v, { decimals: PCT_SHARE, signed: false });
const SignedMove = ({ dollar, pct, cur, color }) => (
  <><Money value={dollar} cur={cur} size={16} weight={700} color={color} signed/> · {fmtPct(pct)}</>
);

function Section({ title, source, children }) {
  return (
    <section style={{ background: UI.surface.card, boxShadow: UI.shadow.cardEl, borderRadius: T.radius.xl, padding: "18px 18px 16px" }}>
      <h2 style={{ ...TYPE.heading, fontSize: 17, margin: "0 0 12px" }}>{title}</h2>
      {children}
      {source && (
        <div style={{ ...TYPE.caption, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${UI.line.hairline}` }}>{source}</div>
      )}
    </section>
  );
}

// `money`: { usd, signed, ils, ilsNote } · the figure is an amount, and its
// shekels go under it (sprint 15, DualMoney).
function Stat({ label, value, tone, money }) {
  const v0 = <LTR className="si-num" style={{ fontSize: 16, fontWeight: 700, color: tone || UI.text.primary }}>{value}</LTR>;
  // A gain since purchase in shekels as the display currency waits for the
  // purchase-day rate, or says why there is none (DualMoney, IlsSinceSlot).
  const v = money?.ilsSince && money.cur === "ILS"
    ? <IlsSinceSlot since={money.ilsSince} size={16} caption={money.ilsCaption}>{v0}</IlsSinceSlot>
    : v0;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12.5, color: UI.text.secondary, marginBottom: 3 }}>{withShekel(label)}</div>
      {money ? <DualMoney usd={money.usd} cur={money.cur} size={16} signed={money.signed} color={tone || UI.text.primary} ils={money.ils} ilsNote={money.ilsNote}
        ilsSince={money.ilsSince} ilsCaption={money.ilsCaption}>{v}</DualMoney> : v}
    </div>
  );
}

const toneOf = v => (v == null || Math.abs(v) < 0.005 ? UI.text.primary : v > 0 ? UI.gain : UI.loss);
const Muted = ({ children }) => <div style={{ fontSize: 14, color: UI.text.secondary, lineHeight: 1.7 }}>{children}</div>;

export default function StockPage({ symbol, name, portfolio = [], watchlist = [], cur = "USD", onClose, lang = "he", userId = null, isGuest = false, watch = null }) {
  const market = useMarketStatus();
  const [data, setData] = useState(null);     // null = loading
  const [news, setNews] = useState(null);     // null = loading
  const [visit, setVisit] = useState(undefined);

  const holding = portfolio.find(p => p.symbol === symbol) || null;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    let alive = true;
    setData(null);
    Promise.all([
      fetchQuote(symbol).catch(() => null),
      fetchProfile(symbol).catch(() => null),
      fetchNextReport(symbol).catch(() => null),
      fetchGeoEvents().catch(() => []),
    ]).then(([quote, profile, report, geo]) => {
      if (alive) setData({ quote, profile, report, geo: geo || [] });
    });
    return () => { alive = false; };
  }, [symbol]);

  const companyName = (holding?.name && holding.name !== symbol ? holding.name : null) || data?.profile?.name || name || "";

  // 5 · News: the company feed, kept only when the article is ABOUT the company.
  useEffect(() => {
    let alive = true;
    setNews(null);
    fetchCompanyNews(symbol, { days: 14 })
      .then(list => { if (alive) setNews(list); })
      .catch(() => { if (alive) setNews([]); });
    return () => { alive = false; };
  }, [symbol]);
  const about = useMemo(
    () => (Array.isArray(news) ? news.filter(a => isAbout(a, symbol, companyName) && !isAdvisoryArticle(a)).slice(0, 8) : null),
    [news, symbol, companyName]);

  // Hebrew headlines from the server, for every company (26/10/2026): an
  // article without one is not listed until it has it (utils/headline.js).
  const { he: heNews, ready: heReady, missing: heMissing } = useHebrewNews(about);

  useEffect(() => {
    let alive = true;
    readBaseline(userId, { isGuest }).then(v => { if (alive) setVisit(v); }).catch(() => { if (alive) setVisit(null); });
    return () => { alive = false; };
  }, [userId, isGuest]);

  // ── 2 · the position ──────────────────────────────────────────────────────
  // In shekels, the gain since purchase at the purchase-day rate only
  // (sprint 13, 5.2 · bug round, 17/10/2026 · utils/purchaseFx.js).
  const purchaseRates = usePurchaseRates(holding ? [holding] : []);
  const ilsToday = useFxRates().ILS?.rate || null;
  const position = useMemo(() => {
    if (!holding) return null;
    // The weight from the same function as "חשיפות התיק", and only once every
    // price is in (sprint 14, 3.1) · a pending holding carries its buy price.
    const settled = !portfolio.some(p => p.quote === "pending");
    const total = settled ? valuedHoldings(portfolio).total : 0;
    const noQuote = holding.quote !== "live" || !(holding.currentPrice > 0);
    const value = noQuote ? null : holding.currentPrice * holding.shares;
    const day = !noQuote && holding.prevClose > 0
      ? { dollar: (holding.currentPrice - holding.prevClose) * holding.shares, pct: ((holding.currentPrice - holding.prevClose) / holding.prevClose) * 100 }
      : null;
    const cost = (holding.buyPrice || 0) * holding.shares;
    const ilsSince = !noQuote ? shekelSince([holding], purchaseRates, ilsToday) : null;
    const ils = ilsSince?.state === "ready" ? ilsSince : null;
    const since = cur === "ILS" && ils ? { dollar: ils.ils / ilsToday, pct: ils.pct, purchaseRate: true, rateDate: ils.rateDates[0], ilsSince }
      : !noQuote && cost > 0 ? { dollar: value - cost, pct: ((value - cost) / cost) * 100, ils: ils ? ils.ils : null, rateDate: ils ? ils.rateDates[0] : null, ilsSince } : null;
    return { weight: value != null && total > 0 ? (value / total) * 100 : null, value, shares: holding.shares, day, since, noQuote, pending: holding.quote === "pending" };
  }, [holding, portfolio, cur, purchaseRates, ilsToday]);

  // ── 3 · what changed, for this company ────────────────────────────────────
  const changes = useMemo(() => {
    if (!holding || visit === undefined || visit === null || !data) return null;
    if (portfolio.some(p => p.quote === "pending")) return null;   // 3.1 · every price first
    return computeChanges({
      portfolio, snapshot: visit.snapshot, geoEvents: data.geo,
      // Same news input as the home screen (utils/todayMoves.js): the last
      // 24 hours, at most three · so the two screens count the same thing.
      news: about ? { [symbol]: about.filter(a => a.datetime * 1000 >= Date.now() - 86400e3).slice(0, 3) } : {},
      since: visit.visitedAt,
      // The home screen's cap of five is for the whole portfolio; one company
      // gets every item that is about it.
      max: Infinity,
    }).filter(c => c.symbol === symbol || (c.symbols || []).includes(symbol));
  }, [holding, visit, data, portfolio, about, symbol]);

  // Judged only once every holding has answered (see HomeTab).
  const fit = useMemo(() => {
    if (!visit || portfolio.some(p => p.quote === "pending")) return { ok: true };
    return checkSnapshot(visit.snapshot, portfolio);
  }, [visit, portfolio]);

  // ── 6 · context ───────────────────────────────────────────────────────────
  const sector = useMemo(() => {
    if (holding?.sector) return holding.sector;
    const p = data?.profile;
    if (!p) return null;
    if (readProfile(p) === "ETF") return SECTOR_ETF;
    return industryToSector(p.finnhubIndustry) || null;
  }, [holding, data]);
  const sectorCtx = useMemo(() => {
    if (!sector) return null;
    const priced = portfolio.filter(p => p.quote !== "none" && p.currentPrice > 0 && p.shares > 0);
    const total = priced.reduce((a, p) => a + p.currentPrice * p.shares, 0);
    const same = priced.filter(p => p.sector === sector);
    const share = total > 0 ? same.reduce((a, p) => a + p.currentPrice * p.shares, 0) / total * 100 : 0;
    return { share, others: same.map(p => p.symbol).filter(s => s !== symbol) };
  }, [sector, portfolio, symbol]);
  const segs = usePortfolioSegments([symbol]);
  const regionCtx = useMemo(() => {
    if (!segs) return undefined;
    const ex = computeExposure([{ symbol, currentPrice: 1, shares: 1 }], segs.segmentsBySymbol);
    if (!ex.covered.count) return null;
    const year = ex.perHolding?.[0]?.year;
    const places = ex.regions.filter(r => r.pct > 0.05 && !["intl_unspecified", "rest_unspecified"].includes(r.id));
    const unsplit = ex.regions.filter(r => ["intl_unspecified", "rest_unspecified"].includes(r.id)).reduce((a, r) => a + r.pct, 0);
    // The company's own regions, in its own words, when we hold its definition.
    const def = companyRegionDef(symbol, year);
    let own = null;
    if (def) {
      const rr = regionRows(segs.segmentsBySymbol[symbol] || []);
      const base = rr.rows.reduce((a, r) => a + (r.revenue > 0 ? r.revenue : 0), 0);
      own = base > 0 ? rr.rows.filter(r => r.revenue > 0 && def.labels[r.segment_name])
        .map(r => ({ label: r.segment_name, ...def.labels[r.segment_name], pct: r.revenue / base * 100 }))
        .sort((a, b) => b.pct - a.pct) : null;
    }
    return { year, places, unsplit, own, source: def?.source };
  }, [segs, symbol]);

  // Price line: the holding's own quote when held, the fetched one otherwise.
  // Only a real quote is labelled as one: the holding's when it is live, the
  // fetched one otherwise. Never the buy-price placeholder, never the price the
  // opener passed along.
  const heldLive = holding && holding.quote === "live" && holding.currentPrice > 0;
  // A symbol that is not held: its quote obeys the same stale rule as a
  // holding's (sprint 13, 5.5 · utils/quoteState.js).
  const staleQuote = !holding && data?.quote?.c > 0 && isStaleQuote(data.quote.t);
  const livePrice = heldLive ? holding.currentPrice : (data?.quote?.c > 0 && !staleQuote ? data.quote.c : null);
  const prevClose = heldLive ? holding.prevClose : data?.quote?.pc;
  const dayPct = livePrice && prevClose > 0 ? ((livePrice - prevClose) / prevClose) * 100 : null;

  const docs = (data?.geo || [])
    .filter(ev => ev.category && ev.category !== "none" && eventNames(ev, symbol, companyName))
    .sort((a, b) => Number(isOfficial(b)) - Number(isOfficial(a)))
    .slice(0, 3);

  const rep = data?.report;

  return (
    <div dir={lang === "he" ? "rtl" : "ltr"} style={{ position: "fixed", inset: 0, zIndex: 400, background: UI.surface.page, overflowY: "auto" }}>
      <div style={{ position: "sticky", top: 0, background: UI.surface.raised, borderBottom: `1px solid ${UI.line.hairline}`, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 2 }}>
        <button onClick={onClose} aria-label="חזרה" style={{ background: "transparent", border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.md, minWidth: 44, minHeight: 44, color: UI.text.secondary, cursor: "pointer", fontSize: 18, fontFamily: "inherit" }}>→</button>
        <span style={{ fontSize: 14, fontWeight: 700, color: UI.text.secondary }}>עמוד חברה</span>
        <div style={{ width: 44 }}/>
      </div>

      <div className="si-fade-in" style={{ maxWidth: 720, margin: "0 auto", padding: "16px 16px 90px", display: "flex", flexDirection: "column", gap: 14 }}>

        {/* 1 · Ticker and name */}
        <header>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <LTR style={{ fontSize: 28, fontWeight: 800, color: UI.text.primary, fontFamily: FONT.latin }}>{symbol}</LTR>
            <HoldingBadge symbol={symbol} portfolio={portfolio} watchlist={watchlist} isHe/>
          </div>
          {companyName && <div style={{ fontSize: 15, color: UI.text.secondary, marginTop: 2 }}><LTR>{cleanName(companyName)}</LTR></div>}
          {livePrice ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
              <Money value={livePrice} cur={cur} size={20} weight={700}/>
              {dayPct != null && (
                Math.abs(dayPct) < 0.005
                  ? <span style={{ fontSize: 13, color: UI.text.muted }}>{market.open ? "ללא שינוי היום" : "ללא שינוי ביום המסחר האחרון"}</span>
                  : <LTR className="si-num" style={{ fontSize: 14, fontWeight: 700, color: toneOf(dayPct) }}>{dayPct > 0 ? "▲ " : "▼ "}{fmtPct(dayPct)}</LTR>
              )}
              <span style={{ ...TYPE.caption }}>מחיר אחרון · <LTR>Finnhub</LTR></span>
            </div>
          ) : staleQuote ? (
            <div style={{ fontSize: 13, color: UI.text.muted, marginTop: 8 }}>{noQuoteText({ lastQuoteAt: data.quote.t })}</div>
          ) : null}
          {/* "מה אם" with this company as a row · today's quantity, no
              direction and no amount (David, 28/09/2026). Only for a symbol
              the app can price: the scenario is computed from the price. */}
          {/* The watchlist, for a symbol that is not held (sprint 13, 5.8). */}
          {!holding && watch?.canEdit && (
            <WatchToggle symbol={symbol} watch={watch}/>
          )}
          {livePrice && (
            <button onClick={() => openScenarioWith(symbol)}
              style={{ ...BTN.secondary, marginTop: 12, minHeight: 44, padding: "10px 16px", display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Icon name="scale" size={15} color={UI.text.secondary}/> בדוק בתרחיש
            </button>
          )}
        </header>

        {/* The price chart (26/10/2026, mockup 2) · under the name, where the
            approved mockup has it. TradingView's full chart is behind "גרף מלא". */}
        <Section title="גרף">
          <PriceChart symbol={symbol}/>
        </Section>

        {/* 2 · The position */}
        {holding && position && (
          <Section title="הפוזיציה שלך" source={<>מחושב מההחזקה שבתיק ומהמחיר האחרון ב-<LTR>Finnhub</LTR>{position?.since?.purchaseRate ? <> · השער היציג ביום הרכישה מבנק ישראל</> : null}</>}>
            {position.noQuote ? (
              <Muted>{position.pending ? "ממתין לציטוט המחיר" : noQuoteText(holding)} · שווי, שינוי ומשקל אינם מחושבים. כמות: <LTR className="si-num">{position.shares}</LTR></Muted>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "14px 12px" }}>
                <Stat label="חלק מהתיק" value={position.weight != null ? pct1(position.weight) : portfolio.some(p => p.quote === "pending") ? "ממתין למחירי התיק" : "לא מחושב"}/>
                <Stat label="שווי" value={<Money value={position.value} cur={cur} size={16} weight={700}/>} money={{ usd: position.value, cur }}/>
                <Stat label="כמות" value={String(position.shares)}/>
                <Stat label={market.open ? "שינוי היום" : lastSessionLabel(market)} value={position.day ? <SignedMove dollar={position.day.dollar} pct={position.day.pct} cur={cur} color={toneOf(position.day.pct)}/> : "אין סגירה קודמת"} tone={toneOf(position.day?.pct)}
                  money={position.day ? { usd: position.day.dollar, cur, signed: true } : null}/>
                {/* Euros and pounds are the dollar gain at today's rate; the
                    shekels are at the purchase-day rate in every currency. */}
                <Stat label={`מאז רכישה · ${PRICE_ONLY_LABEL}${cur === "ILS"
                    ? ` · ${PURCHASE_RATE_LABEL}${position.since?.purchaseRate ? ` (${dateNumeric(position.since.rateDate)})` : ""}`
                    : `${cur === "USD" ? "" : ` · ${TODAY_RATE_LABEL}`}${ilsToday ? ` · ₪ ${PURCHASE_RATE_LABEL}` : ""}`}`}
                  value={position.since ? <SignedMove dollar={position.since.dollar} pct={position.since.pct} cur={cur} color={toneOf(position.since.pct)}/> : "אין מחיר רכישה"} tone={toneOf(position.since?.pct)}
                  money={position.since ? { usd: position.since.dollar, cur, signed: true, ils: position.since.ils ?? null,
                    ilsNote: position.since.rateDate ? `${PURCHASE_RATE_LABEL} (${dateNumeric(position.since.rateDate)})` : PURCHASE_RATE_LABEL,
                    ilsSince: position.since.ilsSince, ilsCaption: FX_EFFECT_LABEL } : null}/>
              </div>
            )}
          </Section>
        )}

        {/* 3 · What changed since the previous visit */}
        {holding && (
          <Section title="מה השתנה מאז הביקור הקודם"
            source={visit?.visitedAt ? <>הביקור הקודם · <LTR className="si-num">{dateNumeric(visit.visitedAt)}</LTR> · אותו צילום מצב שדף הבית משווה אליו</> : null}>
            {visit === undefined || (visit && (!data || portfolio.some(p => p.quote === "pending"))) ? (
              <Skeleton h={22}/>
            ) : visit === null ? (
              <Muted>זהו הביקור הראשון שלך כאן. מהביקור הבא יופיעו כאן שינויים בחברה הזו: תנועת מחיר, שינוי בחלקה בתיק, דוח שפורסם, מסמך רשמי וכתבות.</Muted>
            ) : !fit.ok && (!changes || changes.length === 0) ? (
              <Muted>{NOT_COMPARABLE}<div style={{ ...TYPE.caption }}>{NOT_COMPARABLE_WHY[fit.reason]}</div></Muted>
            ) : !changes || changes.length === 0 ? (
              <Muted>לא השתנה משהו מהותי בחברה הזו מאז הביקור הקודם.</Muted>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {!fit.ok && (
                  <Muted>{NOT_COMPARABLE}<div style={{ ...TYPE.caption }}>{NOT_COMPARABLE_WHY[fit.reason]} · מחירים ומשקלות לא הושוו</div></Muted>
                )}
                {changes.map(c => (
                  <div key={c.id} style={{ padding: "8px 0", borderBottom: `1px solid ${UI.line.hairline}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
                      <span style={{ fontSize: 14.5, color: UI.text.primary }}>{c.text}</span>
 {c.figure && c.figureRtl ? <span dir="rtl" style={{ fontSize: 14.5, fontWeight: 700, color: UI.text.primary }}>מ-<LTR className="si-num">{c.from.toFixed(1)}%</LTR> ל-<LTR className="si-num">{c.to.toFixed(1)}%</LTR></span> : c.figure && <LTR className="si-num" style={{ fontSize: 14.5, fontWeight: 700, color: c.kind === "price" ? toneOf(c.move) : UI.text.primary }}>{c.kind === "report" ? dateNumeric(c.figure) : c.figure}</LTR>}
                    </div>
                    <div style={{ ...TYPE.caption }}>מקור: <span dir="auto">{c.source}</span></div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* 4 · What is coming */}
        <Section title="אירועים קרובים" source={<>מקור: לוח הדוחות של <LTR>Finnhub</LTR></>}>
          {!data ? (
            <Skeleton h={22}/>
          ) : !rep ? (
            <Muted>לוח הדוחות לא ענה כרגע · אין מידע על הדוח הבא</Muted>
          ) : rep.none ? (
            <Muted>אין דוח מתוזמן ב-120 הימים הקרובים</Muted>
          ) : (
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", fontSize: 14.5, color: UI.text.primary }}>
              <span>הדוח הבא</span>
              <LTR className="si-num" style={{ fontWeight: 700 }}>{dateNumeric(rep.date)}</LTR>
              {daysUntilText(rep.date) && <span style={{ color: UI.text.secondary }}>· {daysUntilText(rep.date)}</span>}
              {/* One run: flex-wrap used to start the second line with "·". */}
              {(HOUR_HE[rep.hour] || (rep.quarter && rep.year)) && (
                <span style={{ color: UI.text.muted, fontSize: 13, whiteSpace: "nowrap" }}>
                  {[HOUR_HE[rep.hour], rep.quarter && rep.year ? `Q${rep.quarter} ${rep.year}` : null].filter(Boolean).join(" · ")}
                </span>
              )}
            </div>
          )}
        </Section>

        {/* 5 · News · only what is about the company */}
        <Section title="חדשות"
          source={<>מקור: חדשות החברה ב-<LTR>Finnhub</LTR> · 14 ימים · רק כתבות שהחברה בכותרת שלהן, בלי הודעות לעיתונות</>}>
          {docs.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
              {docs.map(ev => {
                const q = quotedHeadline(ev);
                return (
                  <div key={ev.source_url} style={{ borderBottom: `1px solid ${UI.line.hairline}`, paddingBottom: 8 }}>
                    <OfficialBadge ev={ev}/>
                    {q?.rtl && <div dir="rtl" style={{ fontSize: 14.5, fontWeight: 700, color: UI.text.primary, lineHeight: 1.45, marginTop: 4 }}>{q.text}</div>}
                    <div style={{ ...TYPE.caption }}>{ev.source_name && <LTR>{ev.source_name}</LTR>}</div>
                  </div>
                );
              })}
            </div>
          )}
          {about === null || (about.length > 0 && !heReady) ? (
            <Loading>{[1, 2, 3].map(i => <Skeleton key={i} h={40} mb={8}/>)}</Loading>
          ) : about.length === 0 ? (
            <Muted>אין כתבות על החברה ב-14 הימים האחרונים</Muted>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {about.map(a => {
                const k = a.id || a.url;
                const title = heNews[k]?.title;
                // The translation is checked as well as the English.
                if (!title || isAdvisoryHeadline(title)) return null;
                return (
                  <div key={k} style={{ padding: "10px 0", borderBottom: `1px solid ${UI.line.hairline}` }}>
                    <Headline text={title} style={{ fontSize: 14.5, fontWeight: 700, color: UI.text.primary, lineHeight: 1.45 }}/>
                    <div style={{ ...TYPE.caption, marginTop: 3 }}>
                      {[a.source, a.datetime ? timeAgoSec(a.datetime) : null].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                );
              })}
              {waitingNote(heMissing) && <div style={{ ...TYPE.caption, marginTop: 8 }}>{waitingNote(heMissing)}</div>}
            </div>
          )}
        </Section>

        {/* 6 · Context in the portfolio · facts only */}
        <Section title="הקשר לתיק"
          source={<>מקור: סקטור לפי הענף ב-<LTR>Finnhub</LTR> · הכנסות לפי אזור מדיווחי החברה ל-<LTR>SEC</LTR></>}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14.5, color: UI.text.primary, lineHeight: 1.7 }}>
            {sector ? (
              <div>
                סקטור · <b>{sector}</b>
                {sectorCtx && sectorCtx.share > 0 && <> · <LTR className="si-num">{pct1(sectorCtx.share)}</LTR> מהתיק בסקטור הזה</>}
              </div>
            ) : (
              <div style={{ color: UI.text.secondary }}>הסקטור של החברה לא סווג</div>
            )}
            {sectorCtx && (
              sectorCtx.others.length > 0
                ? <div>החזקות אחרות באותו סקטור · <LTR style={{ fontWeight: 700 }}>{sectorCtx.others.join(" · ")}</LTR></div>
                : holding && <div style={{ color: UI.text.secondary }}>אין בתיק החזקות אחרות באותו סקטור</div>
            )}
            {regionCtx === undefined ? null : regionCtx === null ? (
              <div style={{ color: UI.text.secondary }}>החברה לא מפרטת הכנסות לפי אזור בדיווחיה ל-SEC</div>
            ) : (
              <div>
                הכנסות לפי אזור{regionCtx.year ? <> · שנת דיווח <LTR className="si-num">{regionCtx.year}</LTR></> : null}:{" "}
                {regionCtx.own ? regionCtx.own.map((r, i) => (
                  <span key={r.label}>{i > 0 && " · "}{r.he} <LTR className="si-num">{pct1(r.pct)}</LTR>{r.note && <span style={{ color: UI.text.secondary }}> ({r.note})</span>}</span>
                )) : regionCtx.places.map((r, i) => (
                  <span key={r.id}>{i > 0 && " · "}{r.id === "other" ? "אזורים אחרים" : REGION_HE[r.id]} <LTR className="si-num">{pct1(r.pct)}</LTR></span>
                ))}
                {regionCtx.own && <div style={{ ...TYPE.caption }}>האזורים כפי שהחברה מגדירה אותם · דוח <LTR>{regionCtx.source.form}</LTR> לשנת <LTR className="si-num">{regionCtx.source.year}</LTR></div>}
                {!regionCtx.own && regionCtx.unsplit > 0.05 && <> · לא פורט <LTR className="si-num">{pct1(regionCtx.unsplit)}</LTR></>}
              </div>
            )}
          </div>
        </Section>

        {/* 7 · The numbers · SEC filings only (public record) */}
        <section>
          <h2 style={{ ...TYPE.heading, fontSize: 17, margin: "4px 0 10px" }}>המספרים</h2>
          <FinancialsPanel symbol={symbol}/>
        </section>

        <div style={{ fontSize: 12, color: UI.text.muted, textAlign: "center", padding: "6px 0" }}>
          מידע בלבד · אינו ייעוץ השקעות
        </div>
      </div>
    </div>
  );
}

function WatchToggle({ symbol, watch }) {
  const [busy, setBusy] = useState(false);
  const on = watch.list.some(w => w.symbol === symbol);
  const act = async () => {
    setBusy(true);
    await (on ? watch.remove(symbol) : watch.add(symbol));
    setBusy(false);
  };
  return (
    <button onClick={act} disabled={busy} aria-pressed={on}
      style={{ ...BTN.secondary, marginTop: 12, marginInlineEnd: 8, minHeight: 44, padding: "10px 16px", display: "inline-flex", alignItems: "center", gap: 8, opacity: busy ? 0.6 : 1 }}>
      <Icon name={on ? "check" : "eye"} size={15} color={UI.text.secondary}/> {on ? "במעקב · הסר" : "הוסף למעקב"}
    </button>
  );
}
